import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '@environments';
import type {
  CaptainsAndManningAgentsResponse,
  FeaturesApiResponse,
  InferenceApiRequestBody,
  InferenceAboutResponse,
  RecalculationApiResponse,
  FetchChangeSummaryDetailsApiResponse,
  LastSyncTimeResponse,
  SaveSummaryOfChangesListApiResponse,
  SaveSummaryOfChangesApiResponse,
  SaveSummaryOfChangesRequestBody,
  SummaryOfChangesApiResponse,
  SummaryOfChangesRequestBody,
  SettingsShipListResponse,
  SettingsShipVisibilityUpdateRequest,
  SettingsShipVisibilityUpdateResponse,
  RiskPredictionFormulaResponse,
  ShipListResponse,
  ShipRiskHistoryResponse,
  VoyageContributingFactorsResponse,
} from '@interfaces';


/** Optional date window for `getShipRiskHistory`; both default to full history (~3 years). */
export interface ShipRiskHistoryQuery {
  /** Inclusive lower bound (ISO `YYYY-MM-DD`). */
  from?: string;
  /** Inclusive upper bound (ISO `YYYY-MM-DD`). */
  to?: string;
}

/**
 * Uses the injected HttpClient so **MsalInterceptor** runs on every request.
 * URLs must be under `BACKEND_API_BASE_URL` (see `provideMsal()` → `protectedResourceMap`).
 */
@Injectable({
  providedIn: 'root',
})
export class StoltApiService {
  constructor(private readonly http: HttpClient) {}

  getShipList(): Observable<ShipListResponse> {
    return this.http.get<ShipListResponse>(`${environment.BACKEND_API_BASE_URL}/ships`);
  }

  /**
   * Settings hide/unhide catalogue (all ships + `isVisible` toggle state).
   * @see app-service `GET /api/settings-ships`
   */
  getSettingsShips(): Observable<SettingsShipListResponse> {
    return this.http.get<SettingsShipListResponse>(
      `${environment.BACKEND_API_BASE_URL}/settings-ships`,
    );
  }

  /**
   * Updates Status ON/OFF (`isVisible`) for one settings ship.
   * @see app-service `POST /api/settings-ships`
   */
  updateSettingsShipVisibility(
    body: SettingsShipVisibilityUpdateRequest,
  ): Observable<SettingsShipVisibilityUpdateResponse> {
    return this.http.post<SettingsShipVisibilityUpdateResponse>(
      `${environment.BACKEND_API_BASE_URL}/settings-ships`,
      body,
    );
  }

  /**
   * Completion time of the latest successful data-sync run (manual or scheduled).
   * @see app-service `GET /api/lastSyncTime`
   */
  getLastSyncTime(): Observable<LastSyncTimeResponse> {
    return this.http.get<LastSyncTimeResponse>(
      `${environment.BACKEND_API_BASE_URL}/lastSyncTime`,
    );
  }

  /**
   * Overview "How risk is calculated" side-panel copy.
   * @see app-service `GET /api/get-risk-prediction-formula`
   */
  getRiskPredictionFormula(): Observable<RiskPredictionFormulaResponse> {
    return this.http.get<RiskPredictionFormulaResponse>(
      `${environment.BACKEND_API_BASE_URL}/get-risk-prediction-formula`,
    );
  }

  /**
   * Downloads the Safety Risk Prediction Simple Guide PDF.
   * @see app-service `GET /api/get-risk-prediction-document`
   */
  getRiskPredictionDocument(): Observable<Blob> {
    return this.http.get(`${environment.BACKEND_API_BASE_URL}/get-risk-prediction-document`, {
      responseType: 'blob',
    });
  }

  /**
   * Feature catalogue for adjustable factors (grouped by captain / crew / ship / other).
   * @see app-service `GET /api/features` — `authorizeRequest` applies (MSAL bearer).
   */
  getFeatures(): Observable<FeaturesApiResponse> {
    return this.http.get<FeaturesApiResponse>(`${environment.BACKEND_API_BASE_URL}/features`);
  }

  /**
   * Reference lists for captain / manning-agent dropdowns on adjustable factors.
   * @see app-service `GET /api/captains-and-manning-agents`
   */
  getCaptainsAndManningAgents(): Observable<CaptainsAndManningAgentsResponse> {
    return this.http.get<CaptainsAndManningAgentsResponse>(
      `${environment.BACKEND_API_BASE_URL}/captains-and-manning-agents`,
    );
  }

  /**
   * SHAP-derived contributing factors for every voyage of a ship (UI-shaped).
   * @see app-service `GET /api/voyage-contributing-factors/:shipCode`
   */
  getVoyageContributingFactors(shipCode: string): Observable<VoyageContributingFactorsResponse> {
    const code = encodeURIComponent(shipCode.trim());
    return this.http.get<VoyageContributingFactorsResponse>(
      `${environment.BACKEND_API_BASE_URL}/voyage-contributing-factors/${code}`,
    );
  }

  /**
   * Aggregated daily risk-score / accidents / near-miss series for the overview chart.
   * Backend returns Highcharts-native `[timestampMs, value]` tuples — fed directly into `series[].data`.
   */
  getShipRiskHistory(
    shipCode: string,
    query: ShipRiskHistoryQuery = {},
  ): Observable<ShipRiskHistoryResponse> {
    let params = new HttpParams().set('shipCode', shipCode);
    if (query.from) params = params.set('from', query.from);
    if (query.to) params = params.set('to', query.to);
    return this.http.get<ShipRiskHistoryResponse>(
      `${environment.BACKEND_API_BASE_URL}/ship-risk-history`,
      { params },
    );
  }

  /**
   * Inference / infra recalculation for edited contributing factors.
   * Local default: `POST http://localhost:3000/api/recalculation` (see `environment.BACKEND_API_BASE_URL`).
   */
  postRecalculation(body: InferenceApiRequestBody): Observable<RecalculationApiResponse> {
    return this.http.post<RecalculationApiResponse>(
      `${environment.BACKEND_API_BASE_URL}/recalculation`,
      body,
    );
  }

  /**
   * Inference model metadata (e.g. version) for the active model.
   * @see app-service `GET /api/inference-about` (proxies inference `/about`).
   */
  getInferenceAbout(): Observable<InferenceAboutResponse> {
    return this.http.get<InferenceAboutResponse>(
      `${environment.BACKEND_API_BASE_URL}/inference-about`,
    );
  }

  /**
   * Persists summary-of-changes edits (adjustable factors + change reasons + inference).
   * @see app-service `POST /api/summary-of-changes` (when deployed).
   */
  postSummaryOfChanges(body: SummaryOfChangesRequestBody): Observable<SummaryOfChangesApiResponse> {
    return this.http.post<SummaryOfChangesApiResponse>(
      `${environment.BACKEND_API_BASE_URL}/summary-of-changes`,
      body,
    );
  }

  /**
   * Persists the summary-of-changes step (change reasons per adjusted factor).
   * @see app-service `POST /api/save-summary-of-changes`
   */
  postSaveSummaryOfChanges(
    body: SaveSummaryOfChangesRequestBody,
  ): Observable<SaveSummaryOfChangesApiResponse> {
    return this.http.post<SaveSummaryOfChangesApiResponse>(
      `${environment.BACKEND_API_BASE_URL}/save-summary-of-changes`,
      body,
    );
  }

  /** Logs page rows (saved summaries from the last year, newest first). */
  fetchSummaryOfChangesList(): Observable<SaveSummaryOfChangesListApiResponse> {
    return this.http.get<SaveSummaryOfChangesListApiResponse>(
      `${environment.BACKEND_API_BASE_URL}/fetch-summary-of-changes`,
    );
  }

  /** Change summary detail (`changeSummeryDetail`) for one saved summary document. */
  fetchChangeSummaryDetails(
    changeSummaryId: string,
  ): Observable<FetchChangeSummaryDetailsApiResponse> {
    const id = encodeURIComponent(changeSummaryId.trim());
    return this.http.get<FetchChangeSummaryDetailsApiResponse>(
      `${environment.BACKEND_API_BASE_URL}/save-summary-of-changes/fetchChangeSummaryDetails/${id}`,
    );
  }
}
